import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NoticeSection from '../project-dashboard/components/NoticeSection';
import { supabase } from '../../lib/supabaseClient';

const PAGE_SIZE = 10;

const NoticesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page') || '1'));
  const offset = useMemo(() => (page - 1) * PAGE_SIZE, [page]);

  const [items, setItems] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('ch_notices')
        .select('id, title, author_name, created_at, files:ch_notice_files(file_path, content_type)')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE);

      if (error) {
        setItems([]);
        setHasNext(false);
        return;
      }

      const rows = data || [];
      setHasNext(rows.length > PAGE_SIZE);
      const pageRows = rows.slice(0, PAGE_SIZE);

      setItems(
        pageRows.map((n) => {
          const img = (n.files || []).find((f) => String(f?.content_type || '').startsWith('image/') && f?.file_path);
          const thumbnailUrl = img?.file_path
            ? supabase.storage.from('notice-files').getPublicUrl(img.file_path).data.publicUrl
            : '';
          return {
            id: n.id,
            title: n.title,
            authorName: n.author_name || '꿈현공장장',
            date: n.created_at ? String(n.created_at).slice(0, 10) : '',
            thumbnailUrl,
          };
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <Seo title="공지사항" canonicalPath="/notices" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        <div className="flex items-start justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">공지사항</h1>
            <p className="text-sm text-muted-foreground mt-1">전체 공지사항을 확인하세요.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : (
          <NoticeSection notices={items} hideHeader />
        )}

        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={() => setSearchParams({ page: String(Math.max(1, page - 1)) })}
            disabled={page <= 1 || isLoading}
            className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
          >
            이전
          </button>

          <div className="text-sm text-muted-foreground caption">페이지 {page}</div>

          <button
            type="button"
            onClick={() => setSearchParams({ page: String(page + 1) })}
            disabled={!hasNext || isLoading}
            className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
          >
            다음
          </button>
        </div>
      </main>
    </div>
  );
};

export default NoticesPage;
